import { useMemo } from "react";
import { GrIcon, GrIconButton, GrStack, GrTypography } from "@jnpmedi/grebob";
import { ColumnDef } from "@tanstack/react-table";
import classNames from "classnames";
import timezone from "dayjs/plugin/timezone";
import { useHistory } from "react-router-dom";
import { convertFileExtToGrIconType, readableCreatedBy, readableKBFileSize, readableKind, readableVersion } from "@newLibs/converting";
import { langHelper, MvnMessage } from "@newLibs/utils";
import { useFolderContext } from "@newHooks/context/folder/folder.context";
import { INCLUDE_DELETED_RESOURCE_QUERY_KEY } from "@newHooks/route/useIncludeDeletedQueryParam";
import { useRoute } from "@newHooks/route/useRoute";
import { ellipsis } from "@assets/styles/layout";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { DocumentContentType } from "@maven-rest/docs/Document.dto";
import { BrowseEntrySummary, BrowseEntryType } from "@maven-rest/docs/Folder.dto";
import { UserEssential } from "@maven-rest/sso/User.dto";
import { compareBigintSafe } from "@maven-helper/functions/Misc.function";
import { JsonHelper } from "@maven-helper/helpers/Json.helper";
import dayjs from "dayjs";
import { uniq } from "lodash";

dayjs.extend(timezone);

export function useArchiverBrowseFolderTable(
  handleOpenContextMenu: (params: { anchor: HTMLElement | null; row: BrowseEntrySummary }) => void
) {
  const history = useHistory();
  const { query } = useRoute();
  const { folderIds } = useFolderContext();
  const loLang = langHelper.getLang();

  const rowLink: (item: BrowseEntrySummary) => string | null = (e) => {
    const folderQuery = [];
    const isIncludeDeletedResource = !!JsonHelper.tryParse<boolean>(String(query[INCLUDE_DELETED_RESOURCE_QUERY_KEY]));

    const resourceId = e.resource.id;
    const uniqueFolderIds = uniq(folderIds);

    const isFolderEntryType = e.type === BrowseEntryType.Folder;

    if (isFolderEntryType && uniqueFolderIds.some((rId) => compareBigintSafe(rId, resourceId))) return null;

    if (isIncludeDeletedResource) {
      folderQuery.push(`${INCLUDE_DELETED_RESOURCE_QUERY_KEY}=${isIncludeDeletedResource}`);
    }

    const baseUri = `/archiver/folders${folderIds.length > 0 ? "/" : ""}${folderIds.map((e) => String(e)).join("/")}`;
    const folderUri = `${baseUri}/${e.resource.id}?${folderQuery.join("&")}`;
    const fileUri = `${baseUri}/files/${e.resource.id}`;

    return e.type === BrowseEntryType.Folder
      ? folderUri
      : !e.resource.isDestroyed && e.type === BrowseEntryType.Document && e.document.contentType === DocumentContentType.DocsPdf
      ? fileUri
      : null;
  };

  const columns: ColumnDef<BrowseEntrySummary, keyof BrowseEntrySummary>[] = useMemo(
    () => [
      {
        accessorKey: "resource.name",
        header: MvnMessage(COMMON_LANG.NAME),
        cell: ({ row, getValue }) => {
          const rowData = row.original;
          const isLink =
            rowData.type === BrowseEntryType.Folder ||
            (!rowData.resource.isDestroyed &&
              rowData.type === BrowseEntryType.Document &&
              rowData.document.contentType === DocumentContentType.DocsPdf);

          return (
            <GrStack
              className={classNames({ "is-destroyed": !!rowData.resource.isDestroyed, "link-text": isLink })}
              justifyContent="flex-start"
              alignItems="center"
              gap={10}
              onClick={() => {
                const gotoUrl = rowLink(row.original);
                gotoUrl && history.push(gotoUrl);
              }}
              style={{ width: 600, minWidth: "100%", overflow: "hidden" }}
            >
              <GrIcon size={20} type={convertFileExtToGrIconType(row.original)} />
              <GrTypography
                title={getValue<string>()}
                type="Content.Body04"
                color={!!rowData.resource.isDestroyed ? "grey030" : isLink ? "linkText" : "grey090"}
                style={ellipsis}
              >
                {getValue<string>()}
              </GrTypography>
            </GrStack>
          );
        },
        size: 600,
      },

      {
        accessorFn: (row) => ("size" in row.resource ? row.resource.size : null),
        id: "resourceSize",
        header: "Size",
        cell: ({ row, getValue }) => {
          const rowData = row.original;

          return (
            <GrStack className={rowData.resource.isDestroyed ? "is-destroyed" : ""} flexDirection="row" alignItems="center">
              <GrTypography type="Content.Body04">{readableKBFileSize(getValue<number>())}</GrTypography>
            </GrStack>
          );
        },
        size: 92,
      },
      {
        accessorKey: "document.version",
        header: "Version",
        cell: ({ row }) => {
          const rowData = row.original;

          return (
            <GrStack className={rowData.resource.isDestroyed ? "is-destroyed" : ""} flexDirection="row" alignItems="center">
              <GrTypography type="Content.Body04">{readableVersion(rowData)}</GrTypography>
            </GrStack>
          );
        },
        size: 92,
      },
      {
        accessorKey: "resource.type",
        header: "Kind",
        cell: ({ row }) => {
          const rowData = row.original;

          return (
            <GrStack className={rowData.resource.isDestroyed ? "is-destroyed" : ""} flexDirection="row" alignItems="center">
              <GrTypography type="Content.Body04">{readableKind(rowData)}</GrTypography>
            </GrStack>
          );
        },
        size: 172,
      },
      {
        accessorKey: "resource.createdBy",
        header: "Created by",
        cell: ({ row, getValue }) => {
          const rowData = row.original;

          return (
            <GrStack className={rowData.resource.isDestroyed ? "is-destroyed" : ""} flexDirection="row" alignItems="center">
              <GrTypography type="Content.Body04">{readableCreatedBy(getValue<UserEssential>())}</GrTypography>
            </GrStack>
          );
        },
        size: 172,
      },
      {
        accessorKey: "resource.createdAt",
        header: "Created at",
        cell: ({ row, getValue }) => {
          const rowData = row.original;
          const dateValue = getValue<Date>();

          return (
            <GrStack className={rowData.resource.isDestroyed ? "is-destroyed" : ""} flexDirection="row" alignItems="center">
              <GrTypography type="Content.Body04">{dateValue ? dayjs(dateValue).format("YYYY/MM/DD HH:mm:ss Z") : "-"}</GrTypography>
            </GrStack>
          );
        },
        size: 172,
      },
      {
        accessorKey: "contextMenu",
        cell: ({ row }) => (
          <GrStack className={row.original.resource.isDestroyed ? "is-destroyed" : ""} justifyContent="flex-end">
            <GrIconButton
              iconType="dot-vertical"
              size={16}
              color="grey040"
              onClick={(e) => handleOpenContextMenu({ anchor: e.currentTarget, row: row.original })}
            />
          </GrStack>
        ),
        header: () => <></>,
        size: 16,
      },
    ],

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loLang, query[INCLUDE_DELETED_RESOURCE_QUERY_KEY]]
  );

  return {
    columns,
  };
}
